import { useState } from 'react'
import DownloadIcon from '@mui/icons-material/Download'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import { downloadTextFile } from '../utils/downloadTextFile'
import { TextFileField } from './TextFileField'

export interface OfflineActivationIssued {
    serialNumber: string
    /** The activation response file's text. */
    response: string
}

export interface OfflineActivationDialogProps {
    open: boolean
    onClose: () => void
    /** Sends the request file's text to the billing server; rejects with the server's error. */
    issue: (request: string) => Promise<OfflineActivationIssued>
    /** The name the response is saved under. */
    responseFileName: (serialNumber: string) => string
    /** The message shown for a rejected request. */
    formatError?: (error: unknown) => string
    onIssued?: (issued: OfflineActivationIssued) => void
}

const defaultFormatError = (error: unknown) =>
    error instanceof Error && error.message ? error.message : 'The activation response could not be created.'

/**
 * Offline standalone activation for an air-gapped appliance (EDGE-7188): the user brings the
 * activation request the appliance's Standalone page produced, and takes back the response the
 * billing server encrypted to that appliance. Nimbra Console and Billing Manager share it; each
 * passes its own API call.
 */
export const OfflineActivationDialog = ({
    open,
    onClose,
    issue,
    responseFileName,
    formatError = defaultFormatError,
    onIssued,
}: OfflineActivationDialogProps) => {
    const [request, setRequest] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string>()
    const [issued, setIssued] = useState<OfflineActivationIssued>()

    const close = () => {
        if (busy) return
        setRequest('')
        setError(undefined)
        setIssued(undefined)
        onClose()
    }

    const download = (result: OfflineActivationIssued) =>
        downloadTextFile(responseFileName(result.serialNumber), `${result.response.trim()}\n`)

    const createResponse = async () => {
        setBusy(true)
        setError(undefined)
        try {
            const result = await issue(request.trim())
            setIssued(result)
            download(result)
            onIssued?.(result)
        } catch (e) {
            setError(formatError(e))
        } finally {
            setBusy(false)
        }
    }

    return (
        <Dialog open={open} onClose={close} maxWidth="sm" fullWidth aria-labelledby="offline-activation-title">
            <DialogTitle id="offline-activation-title">Offline activation</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <DialogContentText>
                        For an appliance without a connection to the billing server. On the appliance&apos;s Standalone
                        page, create an activation request and bring the file here. The response is encrypted to that
                        appliance and opens nowhere else; upload it on the same page.
                    </DialogContentText>
                    {error ? (
                        <Alert severity="error" onClose={() => setError(undefined)}>
                            {error}
                        </Alert>
                    ) : null}
                    {issued ? (
                        <Alert
                            severity="success"
                            action={
                                <Button
                                    color="inherit"
                                    size="small"
                                    startIcon={<DownloadIcon />}
                                    onClick={() => download(issued)}
                                >
                                    Download again
                                </Button>
                            }
                        >
                            The activation response for {issued.serialNumber} was downloaded. Upload it on the
                            appliance&apos;s Standalone page.
                        </Alert>
                    ) : null}
                    <TextFileField
                        value={request}
                        onChange={(value) => {
                            setRequest(value)
                            setIssued(undefined)
                        }}
                        onFileError={setError}
                        dropLabel="Drop the activation request file here"
                        placeholder="Or paste the activation request here"
                        disabled={busy}
                        data-test-id="offline-activation-request"
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={close} disabled={busy}>
                    {issued ? 'Close' : 'Cancel'}
                </Button>
                <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    disabled={busy || !request.trim()}
                    loading={busy}
                    onClick={() => void createResponse()}
                >
                    Create response
                </Button>
            </DialogActions>
        </Dialog>
    )
}
